/**
 * R6 §7.3 — the 403 panel. NEW for this shell, tokens only.
 *
 * ⛔ Deep-linking to a screen you do not hold must SAY SO. The mockups make this an explicit
 * shell property: "Opening a screen you do not hold renders a 403 panel… not a blank page and
 * not a hidden link." So this is rendered in place, never a silent `<Navigate>` — a redirect
 * teaches the operator that the URL they were sent does not exist, which is a different and
 * false claim.
 *
 * ⛔ It offers NO persona switcher. That is mockup-only scaffolding whose own README calls it
 * "scaffolding for review… must never ship" (D-17).
 *
 * It does offer the session's own role switch (plan 2026-10-22-cp-active-role, direction 5): when
 * the person holds a site role that is not active, a "Switch role" button opens the account menu
 * on its "Act as" group. The cause line then tells the truth about the person (review F4): one of
 * three sentences, and never "ask a platform admin" to a person who holds the role.
 *
 *   1. An elevation ended in this tab: "Your platform_admin elevation ended at 18:40. Switch to
 *      platform_admin to open this page."
 *   2. The person holds `platform_admin` and acts as a lower role: "You act as fleet_operator.
 *      Switch to platform_admin to open this page." `platform_admin` holds every site permission.
 *   3. The person holds another role that is not active, and no `platform_admin`: "You act as
 *      app_developer. Another site role you hold may open this page." The panel cannot tell which
 *      role opens the page, so it says "may".
 *
 * In cases 2 and 3, when the person's own switch from this tab set the role, the first sentence
 * reads "You switched to fleet_operator at 16:35." (review F6).
 *
 * Outside an `ActiveRoleProvider`, with no other held role, or with `platform_admin` active, it
 * renders as before.
 */
import { ShieldOff } from 'lucide-react';
import { ButtonLink } from './ButtonLink';
import { Button } from './Button';
import { PLATFORM_ADMIN_ROLE } from '@zion/sdk-contracts';
import { actingAsText, clockOf, useActiveRole, type ActiveRoleValue } from './active-role-context';

/** The role that holds every site permission, so a switch to it opens any page. */
const FULL_ROLE = PLATFORM_ADMIN_ROLE;

/**
 * The cause sentence of a closed page for a person who can switch, per the three cases of the file
 * header. Pure.
 */
export function switchCauseText(value: ActiveRoleValue): string {
  const { ended, switched, activeRole, heldSiteRoles } = value;
  // A role that the person no longer holds cannot be switched to, so the ended line does not offer it.
  if (ended !== null && heldSiteRoles.includes(ended.role)) {
    const name = actingAsText(ended.role);
    return `Your ${name} elevation ended at ${clockOf(ended.atMs)}. Switch to ${name} to open this page.`;
  }
  const now =
    switched !== null && switched.role === activeRole
      ? `You switched to ${actingAsText(activeRole)} at ${clockOf(switched.atMs)}.`
      : `You act as ${actingAsText(activeRole)}.`;
  return heldSiteRoles.includes(FULL_ROLE) && activeRole !== FULL_ROLE
    ? `${now} Switch to ${actingAsText(FULL_ROLE)} to open this page.`
    : `${now} Another site role you hold may open this page.`;
}

export interface NotAuthorizedPanelProps {
  /**
   * What was asked for, named on screen. Without it the panel is a dead end: an operator who
   * followed a stale link cannot tell whether they mistyped, whether the resource was deleted,
   * or whether someone sent them a link meant for a colleague.
   */
  readonly requested: string;
  /** Where "go back" goes — always `landingPath(landing)`, so it is somewhere they CAN see. */
  readonly landingTo: string;
}

export function NotAuthorizedPanel({
  requested,
  landingTo,
}: NotAuthorizedPanelProps): React.JSX.Element {
  const active = useActiveRole();
  /*
   * A switch can open the page only from a role below FULL_ROLE: FULL_ROLE holds every site
   * permission, so no other held role opens a page that it cannot.
   */
  const canSwitch =
    active !== null && active.activeRole !== FULL_ROLE && active.heldSiteRoles.some((role) => role !== active.activeRole);
  return (
    <div className="flex h-full items-center justify-center p-8">
      {/*
        `role="alert"` + `aria-live="assertive"`: unlike ZeroAccessPanel's five states, this
        component MOUNTS rather than swapping content inside a stable box, and a screen reader
        user who has just navigated needs to know the navigation did not do what they asked.
        `aria-atomic` so the title and the requested path read as one message.
      */}
      <div
        data-testid="not-authorized"
        role="alert"
        aria-live="assertive"
        aria-atomic="true"
        className="flex max-w-lg flex-col items-center gap-3 rounded-xl border border-state-critical/30 bg-surface p-10 text-center"
      >
        <ShieldOff className="h-6 w-6 text-state-critical" aria-hidden />
        <h1 className="text-sm font-medium text-primary">You don&apos;t have access to this screen</h1>
        <div className="max-w-[52ch] text-xs text-muted">
          {canSwitch ? (
            <p className="text-secondary" data-testid="not-authorized-switch-cause">
              {switchCauseText(active)}
            </p>
          ) : (
            <p>
              Your account doesn&apos;t hold the access this screen needs. If you expected it, ask a
              platform admin to grant it, and include the address below.
            </p>
          )}
          {/*
            ⚠️ The requested path is rendered as TEXT in a font-mono block, never as a link and
            never interpolated into markup. It is a router path this component was handed, but
            treating it as inert data here means a future caller passing something
            user-influenced cannot turn this panel into an injection surface.
          */}
          <p className="mt-2 break-all font-mono text-2xs text-secondary" data-testid="not-authorized-requested">
            {requested}
          </p>
        </div>
        {/* One button size for both actions (review F5): Switch role primary, the way back ghost. */}
        <div className="flex flex-wrap items-center justify-center gap-2">
          {canSwitch ? (
            <>
              <Button tone="primary" size="md" onClick={active.requestRoleMenu} data-testid="not-authorized-switch-role">
                Switch role
              </Button>
              <ButtonLink tone="ghost" size="md" to={landingTo} data-testid="not-authorized-landing-link">
                Back to where you can go
              </ButtonLink>
            </>
          ) : (
            <ButtonLink to={landingTo} data-testid="not-authorized-landing-link">
              Back to where you can go
            </ButtonLink>
          )}
        </div>
      </div>
    </div>
  );
}
